import {
  ApolloClient,
  HttpLink,
  type NormalizedCacheObject,
  from,
  fromPromise,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { createCache, restoreCache } from "./cache";
import { OfflineQueueLink } from "./offlineLink";
import { tokenStore } from "./tokens";
import { UploadLink } from "./uploadLink";
import { refreshAccessToken } from "../services/auth";

/**
 * Apollo Client. Link chain: error → offline-queue → auth(Bearer) → upload → http.
 * The cache is persisted to localforage and restored before the client is
 * returned, so the first paint can render cached data while offline.
 */
const uri = import.meta.env.VITE_GRAPHQL_URI ?? "/graph";

const httpLink = new HttpLink({
  uri,
  // Send the th_token session cookie set by loginUser/createUser.
  credentials: "include",
});

// Sends operations whose variables carry a File/Blob (e.g. createPost media) as
// a multipart request; non-file operations fall through to httpLink.
const uploadLink = new UploadLink(uri);

const authLink = setContext((_op, { headers }) => {
  const token = tokenStore.access;
  return { headers: { ...headers, ...(token ? { authorization: `Bearer ${token}` } : {}) } };
});

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    // An expired access token → try the refresh token once, then retry the op.
    if (graphQLErrors.some((e) => e.extensions?.code === "UNAUTHENTICATED")) {
      return fromPromise(refreshAccessToken())
        .filter((ok) => ok === true)
        .flatMap(() => forward(operation));
    }
    for (const err of graphQLErrors) console.warn(`[graphql] ${err.message}`, err.path);
  }
  if (networkError) console.warn("[network]", networkError.message);
});

export async function createApolloClient(): Promise<ApolloClient<NormalizedCacheObject>> {
  const cache = createCache();
  await restoreCache(cache);

  return new ApolloClient({
    link: from([errorLink, new OfflineQueueLink(), authLink, uploadLink, httpLink]),
    cache,
  });
}
